import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import useTokenStore from "store/tokenStore";

const downloadBlob = (blob, filename) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", filename);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};

const filenameFromDisposition = (header, fallback) => {
	if (!header) return fallback;
	const match = /filename="?([^"]+)"?/i.exec(header);
	return match?.[1] || fallback;
};

const parseBlobError = async (error) => {
	const data = error?.response?.data;
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			const json = JSON.parse(text);
			return {
				message: json?.message,
				errors: json?.data,
			};
		} catch {
			return { message: "Could not download report." };
		}
	}
	return {
		message: error?.response?.data?.message || "Could not download report.",
		errors: error?.response?.data?.data,
	};
};

const useDownloadBulkImportReport = () => {
	const { accessToken } = useTokenStore();

	return useMutation(
		async (importId) => {
			const instance = await InitApi({ accessToken });
			const response = await instance.get(
				`/publisher/bulk-book-imports/${importId}/report`,
				{ responseType: "blob" }
			);

			const contentType = response.headers["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await response.data.text();
				const json = JSON.parse(text);
				throw Object.assign(new Error(json?.message || "Download failed"), {
					response: { data: json },
				});
			}

			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const filename = filenameFromDisposition(
				response.headers["content-disposition"],
				`bulk_book_import_report_${importId}.xlsx`
			);
			downloadBlob(blob, filename);
			toast.success("Report downloaded");
			return true;
		},
		{
			onError: async (error) => {
				const parsed = await parseBlobError(error);
				toast.error(
					<ServerErrors
						errorMessage={parsed.message}
						errors={parsed.errors}
					/>
				);
			},
		}
	);
};

export default useDownloadBulkImportReport;
